import { useMemo, useState } from 'react'
import { feature } from 'topojson-client'
import { geoPath } from 'd3-geo'
import world from '@/assets/world.topojson'
import { fitMercatorToBbox, projectLatLon } from '@/lib/geo'
import { getCountryLabel, getCountryGroup } from '@/assets/region-mapping'
import type { ThreatMapData } from '@/types/map'

export interface CountryMapProps {
  data: ThreatMapData
  iso2: string
  onDrillSite: (targetId: string) => void
  isLoading: boolean
  error: unknown
}

/**
 * Natural-Earth 110m TopoJSON only carries `iso_a2` on ~69/177 features.
 * Mirror of the numeric→alpha2 table used in RegionMap. Kept local per file
 * rather than shared so the PR-2B Maps agent owns all edits to these two
 * files; a future refactor can lift this into a shared module.
 */
const NUMERIC_TO_ISO2: Record<string, string> = {
  '004': 'AF', '010': 'AQ', '024': 'AO', '031': 'AZ', '044': 'BS',
  '051': 'AM', '064': 'BT', '068': 'BO', '070': 'BA', '072': 'BW',
  '084': 'BZ', '090': 'SB', '096': 'BN', '104': 'MM', '108': 'BI',
  '116': 'KH', '120': 'CM', '140': 'CF', '144': 'LK', '148': 'TD',
  '178': 'CG', '180': 'CD', '188': 'CR', '192': 'CU', '196': 'CY',
  '204': 'BJ', '214': 'DO', '218': 'EC', '222': 'SV', '226': 'GQ',
  '232': 'ER', '238': 'FK', '242': 'FJ', '260': 'TF', '262': 'DJ',
  '266': 'GA', '268': 'GE', '270': 'GM', '275': 'PS', '288': 'GH',
  '304': 'GL', '320': 'GT', '324': 'GN', '328': 'GY', '332': 'HT',
  '340': 'HN', '352': 'IS', '372': 'IE', '384': 'CI', '388': 'JM',
  '398': 'KZ', '410': 'KR', '414': 'KW', '417': 'KG', '418': 'LA',
  '422': 'LB', '426': 'LS', '430': 'LR', '434': 'LY', '442': 'LU',
  '450': 'MG', '454': 'MW', '466': 'ML', '478': 'MR', '496': 'MN',
  '498': 'MD', '499': 'ME', '508': 'MZ', '512': 'OM', '516': 'NA',
  '524': 'NP', '540': 'NC', '548': 'VU', '558': 'NI', '562': 'NE',
  '591': 'PA', '598': 'PG', '600': 'PY', '624': 'GW', '626': 'TL',
  '630': 'PR', '634': 'QA', '646': 'RW', '686': 'SN', '694': 'SL',
  '706': 'SO', '716': 'ZW', '728': 'SS', '729': 'SD', '732': 'EH',
  '740': 'SR', '748': 'SZ', '762': 'TJ', '768': 'TG', '780': 'TT',
  '788': 'TN', '795': 'TM', '800': 'UG', '807': 'MK', '834': 'TZ',
  '854': 'BF', '858': 'UY', '860': 'UZ', '887': 'YE', '894': 'ZM',
}

function resolveIso2(featureObj: {
  id?: string | number
  properties?: Record<string, unknown>
}): string {
  const props = featureObj.properties ?? {}
  const iso = props.iso_a2 ?? props.ISO_A2 ?? props.ISO3166_1_ALPHA2
  if (typeof iso === 'string' && iso.length === 2) return iso
  const id = featureObj.id != null ? String(featureObj.id).padStart(3, '0') : ''
  return NUMERIC_TO_ISO2[id] ?? ''
}

export function CountryMap({
  data,
  iso2,
  onDrillSite,
  isLoading,
  error,
}: CountryMapProps) {
  const W = 800
  const H = 600
  const group = useMemo(() => getCountryGroup(iso2), [iso2])
  const countries = useMemo(
    () =>
      (
        feature(
          world as unknown as Parameters<typeof feature>[0],
          (world as unknown as { objects: Record<string, unknown> }).objects.countries as Parameters<
            typeof feature
          >[1],
        ) as unknown as {
          features: Array<{ id?: string; properties?: Record<string, unknown> }>
        }
      ).features,
    [],
  )

  const bbox = useMemo<[number, number, number, number]>(() => {
    let minLon = 180
    let minLat = 90
    let maxLon = -180
    let maxLat = -90
    for (const f of countries) {
      const code = resolveIso2(f)
      if (!group.includes(code)) continue
      const b = geoPath().bounds(f as unknown as Parameters<ReturnType<typeof geoPath>['bounds']>[0])
      if (!b || !Number.isFinite(b[0][0])) continue
      minLon = Math.min(minLon, b[0][0])
      minLat = Math.min(minLat, b[0][1])
      maxLon = Math.max(maxLon, b[1][0])
      maxLat = Math.max(maxLat, b[1][1])
    }
    if (minLon === 180 || maxLon === -180) {
      // fallback to a small square around the world origin if no group
      // feature matched — keeps the component robust against topology drift
      return [-10, -10, 10, 10]
    }
    return [minLon - 2, minLat - 2, maxLon + 2, maxLat + 2]
  }, [countries, group])

  const proj = useMemo(
    () => fitMercatorToBbox(bbox, { width: W, height: H, padding: 32 }),
    [bbox],
  )
  const path = useMemo(() => geoPath(proj), [proj])
  const [hover, setHover] = useState<string | null>(null)

  if (isLoading) {
    return (
      <div
        data-testid="country-skeleton"
        className="h-full w-full animate-pulse bg-[color:var(--surface-secondary)]"
      />
    )
  }
  if (error) {
    return (
      <div className="p-4 text-[color:var(--severity-high)]">Failed to load country data.</div>
    )
  }

  const incidentsInGroup = data.incidents.filter((inc) => {
    const t = data.targets.find((x) => x.id === inc.targetId)
    return t && group.includes(t.countryIso2)
  })

  return (
    <div className="relative h-full w-full">
      <div
        data-testid="country-header"
        className="pointer-events-none absolute left-1/2 top-3 z-10 -translate-x-1/2 rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-secondary)]/85 px-3 py-1 text-center backdrop-blur"
      >
        <div className="font-mono text-xs uppercase tracking-[0.16em] text-[color:var(--text-primary)]">
          {getCountryLabel(iso2)}
        </div>
        <div className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.14em] text-[color:var(--text-tertiary)]">
          {data.targets.filter((t) => group.includes(t.countryIso2)).length} assets ·{' '}
          {incidentsInGroup.length} live events
        </div>
      </div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={`${getCountryLabel(iso2)} country view`}
        className="h-full w-full"
      >
        {countries.map((c, i) => {
          const code = resolveIso2(c)
          const focus = code ? group.includes(code) : false
          return (
            <path
              key={i}
              data-iso2={code || undefined}
              data-focus={focus ? 'true' : 'false'}
              d={path(c as unknown as Parameters<typeof path>[0]) ?? ''}
              fill={focus ? 'var(--surface-secondary)' : 'var(--surface-tertiary)'}
              fillOpacity={focus ? 1 : 0.6}
              stroke={focus ? 'var(--interactive-primary)' : 'var(--border-subtle)'}
              strokeWidth={focus ? 1 : 0.4}
            />
          )
        })}
        {incidentsInGroup.map((inc) => {
          const t = data.targets.find((x) => x.id === inc.targetId)!
          const p = projectLatLon(proj, t.latitude, t.longitude)
          if (!p) return null
          return (
            <g
              key={inc.id}
              data-testid="country-marker"
              onMouseEnter={() => setHover(inc.id)}
              onMouseLeave={() => setHover(null)}
              onClick={() => onDrillSite(t.id)}
              style={{ cursor: 'pointer' }}
            >
              <circle
                cx={p[0]}
                cy={p[1]}
                r={10}
                fill={`var(--severity-${inc.severity})`}
                fillOpacity={0.3}
              />
              <circle cx={p[0]} cy={p[1]} r={4} fill={`var(--severity-${inc.severity})`} />
            </g>
          )
        })}
      </svg>
      {hover &&
        (() => {
          const inc = incidentsInGroup.find((x) => x.id === hover)
          const t = inc && data.targets.find((x) => x.id === inc.targetId)
          if (!t) return null
          return (
            <div
              role="tooltip"
              className="pointer-events-none absolute left-4 top-4 rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-elevated)] px-2 py-1 text-xs"
            >
              {t.name} — {t.incidentCount} incidents
            </div>
          )
        })()}
    </div>
  )
}
